import React, { useState, useEffect } from 'react';
import Input from '../Form/Input';
import PhoneInput from '../Form/PhoneInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { inviteCoach, resetInviteSuccess } from '../../store/AdminSlices/coachSlice';
import { useToast } from '../Toast/ToastContainer';

interface AddCoachFormProps {
  onClose: () => void;
  onSubmit: (data: CoachFormData) => void;
}

export interface CoachFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  areaOfExpertise: string;
  completedSessions: string;
  lastSession: string;
  additionalNotes: string;
}

const AddCoachForm: React.FC<AddCoachFormProps> = ({ onClose, onSubmit }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [invitedCoachName, setInvitedCoachName] = useState('');
  const dispatch = useAppDispatch();
  const { loading, error, inviteSuccess } = useAppSelector((state) => state.coach);
  const { showToast } = useToast();

  const [formData, setFormData] = useState<CoachFormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    areaOfExpertise: '',
    completedSessions: '0',
    lastSession: '',
    additionalNotes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle successful invite
  useEffect(() => {
    if (inviteSuccess) {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      setInvitedCoachName(fullName || 'Coach');
      setShowSuccessModal(true);
      onSubmit(formData);
      dispatch(resetInviteSuccess());
    }
  }, [inviteSuccess, dispatch, onSubmit, formData]);

  // Handle error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.areaOfExpertise) newErrors.areaOfExpertise = 'Area of expertise is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Dispatch the invite coach action with API payload format
      await dispatch(
        inviteCoach({
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          area_of_expertise: formData.areaOfExpertise,
          notes: formData.additionalNotes,
        })
      );
    }
  };

  const handleChange = (field: keyof CoachFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return (
      formData.email.trim() !== '' &&
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.areaOfExpertise.trim() !== ''
    );
  };

  return (
    <div 
      className="bg-white rounded-xl border border-[#F2F4F7] p-5 shadow-[0_4px_12px_0_rgba(249,250,251,1)]"
    
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 ">
        {/* Basic Information */}
        <div>
                     <h3 className="mb-4 border-b border-gray-200 pb-4 text-sm font-medium text-gray-900">Basic Information</h3>
          <div className="space-y-4">
            <div className="max-w-sm">
              <Input
                label="Email"
                placeholder="coach@example.com"
                value={formData.email}
                onChange={(value) => handleChange('email', value)}
                type="email"
                required
                error={errors.email}
                disabled={loading}
              />
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Coach will receive login credentials and all platform updates at this email.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="First Name"
                placeholder="Charles"
                value={formData.firstName}
                onChange={(value) => handleChange('firstName', value)}
                required
                error={errors.firstName}
                disabled={loading}
              />
              <Input
                label="Last Name"
                placeholder="Lawrence"
                value={formData.lastName}
                onChange={(value) => handleChange('lastName', value)}
                required
                error={errors.lastName}
                disabled={loading}
              />
              <PhoneInput
                label="Phone"
                placeholder="Enter Coach's Phone Number"
                value={formData.phone}
                onChange={(value) => handleChange('phone', value)}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Coaching Information */}
        <div>
          <h3 className="mb-4 text-sm font-medium text-gray-900">Coaching Information</h3>
          <div className="space-y-4">
            <div>
              <Input
                label="Area of Expertise"
                placeholder="Enter Area of Expertise"
                value={formData.areaOfExpertise}
                onChange={(value) => handleChange('areaOfExpertise', value)}
                required
                error={errors.areaOfExpertise}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Additional Notes</label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => handleChange('additionalNotes', e.target.value)}
                placeholder="Add any additional notes about the coach"
                rows={3}
                disabled={loading}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              isFormValid() 
                ? 'bg-gray-900 hover:bg-gray-800' 
                : 'bg-gray-400 hover:bg-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Send Invitation
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <svg
                className="h-7 w-7 text-emerald-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 9H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="text-center text-xl font-semibold text-gray-900">Coach Invite Sent</h2>
            <p className="mt-2 text-center text-sm text-gray-500">
              You will be notified when <span className="font-semibold text-gray-900">{invitedCoachName}</span> accepts
              the invite and log in to his account
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
              }}
              className="mt-6 w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-gray-800"
            >
              Back to Coaches
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCoachForm;
